import styleIcon from 'uiComponents/studio/icons/style.svg'
import copyIcon from 'uiComponents/studio/icons/copy.svg'
import restrictionsIcon from 'uiComponents/studio/icons/target.svg'
import settingsIcon from 'uiComponents/studio/icons/settings.svg'
import { SectionObject } from 'uiComponents/studio/menuItem'
import { FieldType } from 'uiComponents/studio/form/fieldComponentSchema'
import { Audience } from 'crm/schema'
import { t } from 'translations'

export interface FormDefinitionSchema {
    name: SectionObject
    notification: SectionObject
    target: SectionObject
    scheduling: SectionObject
}

export const formDefinition = (audiences: Audience[]): FormDefinitionSchema => {
    return {
        name: {
            name: 'name',
            label: t('notificationCampaigns.studio.steps.name'),
            header: t('notificationCampaigns.studio.header'),
            icon: copyIcon,
            description: t('notificationCampaigns.studio.descriptions.name'),
            sections: [
                {
                    name: t('notificationCampaigns.studio.sections.name'),
                    fields: [
                        {
                            name: 'name',
                            type: 'text',
                            label: t('notificationCampaigns.studio.fields.campaignName'),
                            span: 12,
                            path: 'campaign.name',
                            defaultValue: '',
                            placeholder: t('notificationCampaigns.studio.fields.campaignNamePlaceholder'),
                        },
                    ],
                },
            ],
        },
        target: {
            name: 'target',
            label: t('notificationCampaigns.studio.steps.target'),
            header: t('notificationCampaigns.studio.header'),
            feature: 'CRMAudiencesPage',
            icon: restrictionsIcon,
            description: t('notificationCampaigns.studio.descriptions.target'),
            sections: [
                {
                    name: t('notificationCampaigns.studio.sections.targeting'),
                    infotip: t('notificationCampaigns.studio.targetingInfotip'),
                    fields: [
                        {
                            name: 'targetType',
                            type: 'multiSelect',
                            availableOptions: audiences.map((audience) => {
                                return {
                                    name: audience.name,
                                    value: audience.slug,
                                }
                            }),
                            label: t('notificationCampaigns.studio.fields.audience'),
                            span: 12,
                            path: 'target.audience',
                            defaultValue: '',
                            infotip: t('notificationCampaigns.studio.fields.audienceInfotip'),
                        },
                        {
                            name: 'marketingConsent',
                            type: 'checkbox',
                            label: t('notificationCampaigns.studio.fields.marketingConsentProvided'),
                            path: 'target.marketingConsent',
                            defaultValue: false,
                        },
                        {
                            name: 'channels',
                            type: 'select',
                            availableOptions: [
                                {
                                    name: t('notificationCampaigns.studio.options.push'),
                                    value: 'Push',
                                },
                                {
                                    name: t('notificationCampaigns.studio.options.chat'),
                                    value: 'Chat',
                                },
                                {
                                    name: t('notificationCampaigns.studio.options.pushAndChat'),
                                    value: 'Push_and_Chat',
                                },
                            ],
                            label: t('notificationCampaigns.studio.fields.channels'),
                            span: 12,
                            path: 'target.channel',
                            defaultValue: '',
                        },
                    ],
                },
            ],
        },
        notification: {
            name: 'notification',
            label: t('notificationCampaigns.studio.steps.notification'),
            header: t('notificationCampaigns.studio.header'),
            icon: styleIcon,
            description: t('notificationCampaigns.studio.descriptions.notification'),
            sections: [
                {
                    name: t('notificationCampaigns.studio.sections.type'),
                    fields: [
                        {
                            name: 'notificationType',
                            type: 'select',
                            availableOptions: [
                                {
                                    name: t('notificationCampaigns.studio.options.plain'),
                                    value: 'Plain',
                                },
                                {
                                    name: t('notificationCampaigns.studio.options.web'),
                                    value: 'Web',
                                },
                            ],
                            label: t('notificationCampaigns.studio.fields.notificationType'),
                            span: 12,
                            path: 'notification.type',
                            defaultValue: '',
                        },
                        {
                            name: 'url',
                            type: 'text',
                            label: t('notificationCampaigns.studio.fields.url'),
                            span: 12,
                            path: 'notification.url',
                            defaultValue: '',
                            placeholder: t('notificationCampaigns.studio.fields.urlPlaceholder'),
                        },
                    ],
                },
                {
                    // name is the section id (form.tsx); label is what the user sees
                    name: 'Languages',
                    label: t('notificationCampaigns.studio.sections.languages'),
                    fields: [],
                },
            ],
        },
        scheduling: {
            name: 'scheduling',
            label: t('notificationCampaigns.studio.steps.scheduling'),
            header: t('notificationCampaigns.studio.header'),
            icon: settingsIcon,
            description: t('notificationCampaigns.studio.descriptions.scheduling'),
            sections: [
                {
                    name: t('notificationCampaigns.studio.sections.scheduling'),
                    fields: [
                        {
                            name: 'type',
                            type: 'select',
                            availableOptions: [
                                {
                                    name: t('notificationCampaigns.studio.options.oneTime'),
                                    value: 'one_time',
                                },
                                {
                                    name: t('notificationCampaigns.studio.options.recurring'),
                                    value: 'recurring',
                                },
                            ],
                            label: t('notificationCampaigns.studio.fields.scheduleType'),
                            span: 12,
                            path: 'schedule.scheduleType',
                            defaultValue: '',
                        },
                        {
                            name: 'schedule.type',
                            type: 'select',
                            availableOptions: [
                                {
                                    name: t('notificationCampaigns.studio.options.days'),
                                    value: 'Days',
                                },
                                {
                                    name: t('notificationCampaigns.studio.options.weeks'),
                                    value: 'Weeks',
                                },
                            ],
                            label: t('notificationCampaigns.studio.fields.repeatEvery'),
                            span: 12,
                            path: 'schedule.whenUnit',
                            defaultValue: '',
                        },
                        {
                            name: 'scheduleWhenMonday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.monday'),
                            span: 12,
                            path: 'schedule.scheduleWhenMonday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenTuesday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.tuesday'),
                            span: 12,
                            path: 'schedule.scheduleWhenTuesday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenWednesday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.wednesday'),
                            span: 12,
                            path: 'schedule.scheduleWhenWednesday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenThursday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.thursday'),
                            span: 12,
                            path: 'schedule.scheduleWhenThursday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenFriday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.friday'),
                            span: 12,
                            path: 'schedule.scheduleWhenFriday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenSaturday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.saturday'),
                            span: 12,
                            path: 'schedule.scheduleWhenSaturday',
                            defaultValue: false,
                        },
                        {
                            name: 'scheduleWhenSunday',
                            type: 'toggle',
                            label: t('notificationCampaigns.studio.fields.sunday'),
                            span: 12,
                            path: 'schedule.scheduleWhenSunday',
                            defaultValue: false,
                        },
                        {
                            name: 'date',
                            type: 'date',
                            label: t('notificationCampaigns.studio.fields.sendOnDate'),
                            span: 12,
                            path: 'schedule.date',
                            defaultValue: '',
                        },
                        {
                            name: 'date-time',
                            type: 'time',
                            label: t('notificationCampaigns.studio.fields.atTime'),
                            span: 12,
                            path: 'schedule.time',
                            defaultValue: '',
                        },
                        {
                            name: 'start-date',
                            type: 'date',
                            label: t('notificationCampaigns.studio.fields.startDate'),
                            span: 12,
                            path: 'schedule.startDate',
                            defaultValue: '',
                        },
                        {
                            name: 'start-date-time',
                            type: 'time',
                            label: t('notificationCampaigns.studio.fields.atTime'),
                            span: 12,
                            path: 'schedule.startTime',
                            defaultValue: '',
                        },
                        {
                            name: 'end-date',
                            type: 'date',
                            label: t('notificationCampaigns.studio.fields.optionalRepeatUntil'),
                            span: 12,
                            path: 'schedule.endDate',
                            defaultValue: '',
                        },
                        {
                            name: 'end-date-time',
                            type: 'time',
                            label: t('notificationCampaigns.studio.fields.atTime'),
                            span: 12,
                            path: 'schedule.endTime',
                            defaultValue: '',
                        },
                    ],
                },
            ],
        },
    }
}

export const getIntervalField = (): FieldType => ({
    name: 'schedule.interval',
    type: 'number',
    label: t('notificationCampaigns.studio.fields.interval'),
    span: 6,
    path: 'schedule.interval',
    defaultValue: '',
    placeholder: t('notificationCampaigns.studio.fields.intervalPlaceholder'),
    min: 0,
    max: 10,
    unit: '',
})
